// useGtm — READ-ONLY hooks for the GTM page's live data.
//
// /api/gtm/* sits behind requireStaffAccess({ product:'united_ai', role:'admin' })
// — the SAME gate as the Genesis admin API — so these reads follow the project's
// proven T6 admin idiom (EnterpriseAgentAccessTab.js / GenesisPage.js):
//   REACT_APP_BACKEND_URL base + Firebase ID token in an Authorization: Bearer header.
//
// Each hook returns { data, loading, error, reload } so a tab can render its own
// loading / empty / error state independently. A 401/403 is surfaced explicitly
// (the admin token isn't attached) rather than collapsing to a blank screen.

import { useCallback, useEffect, useState } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import axios from 'axios';

// T6 admin API base. Matches apiService.js / EnterpriseAgentAccessTab.js.
export const T6_BASE = process.env.REACT_APP_BACKEND_URL || 'https://api.mgmt.joinunited.ai';

export async function authHeader() {
  const user = getAuth().currentUser;
  if (!user) throw new Error('Not authenticated. Please sign in again.');
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

// Turn an axios error into a readable message, calling out auth failures plainly
// so a missing/expired admin token reads as exactly that.
function readError(err) {
  const status = err && err.response && err.response.status;
  if (status === 401 || status === 403) {
    return `Not authorized (${status}). Your admin access token isn't attached — sign in with a staff account that has united_ai admin access.`;
  }
  return (
    (err && err.response && err.response.data &&
      (err.response.data.message || err.response.data.detail || err.response.data.error)) ||
    (err && err.message) ||
    'Request failed.'
  );
}

// Generic GTM GET hook. `pick` maps the response body to the value the page
// wants (e.g. (d) => d.campaigns). Fires once on mount; `reload` re-runs it.
export function useGtmResource(path, pick) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await authHeader();
      const res = await axios.get(`${T6_BASE}${path}`, { headers });
      setData(pick ? pick(res.data) : res.data);
    } catch (err) {
      setError(readError(err));
      setData(null);
    } finally {
      setLoading(false);
    }
    // `pick` is defined inline at each call site and intentionally excluded
    // from deps; `path` is the stable identity of the resource.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, reload: load };
}

// useGtmModels — the allowed drafting models for the T5 selectors (per-card
// "regenerate with" + the global default). Sourced from GET /api/gtm/models so
// the list is NEVER hardcoded; returns { models: string[], defaultModel, ... }.
export function useGtmModels() {
  const [models, setModels] = useState([]);
  const [defaultModel, setDefaultModel] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await authHeader();
      const res = await axios.get(`${T6_BASE}/api/gtm/models`, { headers });
      setModels(Array.isArray(res.data.models) ? res.data.models : []);
      setDefaultModel(res.data.default || '');
    } catch (err) {
      setError(readError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { models, defaultModel, loading, error, reload: load };
}

// regenerateOpener — POST /api/gtm/contacts/:id/regenerate { model }. Recomposes
// ONE contact's opener with the requested model and resolves to the new opener
// { text, subject, modelUsed }. Throws a readable message on failure so the card
// can surface it inline. The stream also emits a superseding 'upgraded' event,
// so the card converges even if this response is missed.
export async function regenerateOpener(id, model) {
  const headers = await authHeader();
  try {
    const res = await axios.post(
      `${T6_BASE}/api/gtm/contacts/${id}/regenerate`,
      { model },
      { headers }
    );
    return res.data.opener; // { text, subject, modelUsed, composedAt }
  } catch (err) {
    throw new Error(readError(err));
  }
}

// Turn an axios error into an Error that also carries the server's stable `code`
// (res.data.error), the HTTP `httpStatus`, and any `serverStatus` (e.g. the
// contact's actual status on a 409 contact_not_pending_approval) so callers can
// special-case a response without re-parsing.
function gtmActionError(err) {
  const data = (err && err.response && err.response.data) || {};
  const e = new Error(readError(err));
  e.httpStatus = (err && err.response && err.response.status) || 0;
  e.code = data.error || '';
  e.serverStatus = data.status || '';
  // Full response body so callers can read coded extras without re-parsing, e.g.
  // a 422 invalid_step carries { step, detail } the Sequences editor renders inline.
  e.data = data;
  return e;
}

// POST /api/gtm/contacts/:id/approve — flip pending_approval -> new. The
// edit-merged opener { text, subject } rides in the body so the approved version
// carries the operator's edits. Resolves the server truth { ok, id, status, opener }.
export async function approveContact(id, opener) {
  const headers = await authHeader();
  const body = opener
    ? { opener: { text: opener.text || '', subject: opener.subject || '' } }
    : {};
  try {
    const res = await axios.post(`${T6_BASE}/api/gtm/contacts/${id}/approve`, body, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// POST /api/gtm/contacts/:id/reject — flip to terminal 'rejected'. Idempotent.
export async function rejectContact(id, reason) {
  const headers = await authHeader();
  try {
    const res = await axios.post(
      `${T6_BASE}/api/gtm/contacts/${id}/reject`,
      reason ? { reason } : {},
      { headers }
    );
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// PATCH /api/gtm/contacts/:id/opener — persist an opener edit without approving.
// Resolves the server truth { ok, id, status, opener } so the card reconciles.
export async function editContactOpener(id, patch) {
  const headers = await authHeader();
  try {
    const res = await axios.patch(
      `${T6_BASE}/api/gtm/contacts/${id}/opener`,
      { text: patch.text || '', subject: patch.subject || '' },
      { headers }
    );
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// POST /api/gtm/campaigns — source a campaign from Apollo and write it as
// pending_approval (sends nothing). Mirrors the other GTM writers: T6_BASE +
// authHeader() Bearer + gtmActionError so callers can special-case coded errors
// (budget_too_low, sourcing_paused, apollo_*, …). The backend coerces budget via
// Number(), so `budget` must be a clean number, not a "$5,000"-style string.
// Returns the created campaign (res.data.campaign).
export async function createCampaign(payload) {
  const headers = await authHeader();
  try {
    const res = await axios.post(`${T6_BASE}/api/gtm/campaigns`, payload, { headers });
    return res.data.campaign;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// GET /api/gtm/campaigns/:id — campaign detail. Used by the approve modal to show
// the cost estimate. Returns { campaign, contactCount, contactCountsByStatus,
// costEstimate } where costEstimate = { expectedCost, ceilingCost, suppressionRate, ... }.
export async function fetchCampaign(id) {
  const headers = await authHeader();
  try {
    const res = await axios.get(`${T6_BASE}/api/gtm/campaigns/${id}`, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// PATCH /api/gtm/campaigns/:id — update campaign-level settings in place. Used for the
// auto-approve toggle (body { autoApprove: boolean }); the field ships in the T6
// campaign directive. Mirrors updateSequence: T6_BASE + authHeader() + gtmActionError.
// Returns res.data (the updated campaign, or { ok, campaign } depending on the route).
export async function updateCampaign(id, patch) {
  const headers = await authHeader();
  try {
    const res = await axios.patch(`${T6_BASE}/api/gtm/campaigns/${id}`, patch, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// POST /api/gtm/campaigns/preview — FREE targeting preview (no reveal, no charge).
// Same payload as createCampaign. Returns { total_entries, previewCount, people,
// targeting:{titles,sizes,locations,naics,persona,rationale}, composed, note }.
export async function previewCampaign(payload) {
  const headers = await authHeader();
  try {
    const res = await axios.post(`${T6_BASE}/api/gtm/campaigns/preview`, payload, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// POST /api/gtm/campaigns/:id/import-contacts — import parsed CSV rows into a
// campaign (cap 500 per request; the caller batches). Body { contacts:[{firstName,
// lastName, email, title, companyName, companyDomain, linkedinUrl}] }. Returns
// { total, imported, suppressedCustomers, rejected:[{index,email,reason}], note }.
export async function importContacts(campaignId, contacts) {
  const headers = await authHeader();
  try {
    const res = await axios.post(
      `${T6_BASE}/api/gtm/campaigns/${campaignId}/import-contacts`,
      { contacts },
      { headers }
    );
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// GET /api/gtm/campaigns/:id/prompts — the campaign's resolved SEQUENCE steps (the single
// source of truth the Sequences tab also edits). Returns { sequenceId, source,
// sequenceStatus, steps:[{ key, stepNumber, subject, body, model, dayOffset, threadMode,
// researchConfig }] } (draftPrompts is a back-compat alias for the same steps).
export async function fetchCampaignPrompts(id) {
  const headers = await authHeader();
  try {
    const res = await axios.get(`${T6_BASE}/api/gtm/campaigns/${id}/prompts`, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// PATCH /api/gtm/campaigns/:id/prompts — persist ONE step's edit. Body { key, subject?,
// body?, model?, dayOffset?, threadMode?, researchConfig? }. The editable copy is the
// template subject + body (`prompt` is only a legacy alias for body). Optional fields are
// sent only when provided; the backend validates each. Resolves { sequenceId, steps:[...] }.
// A 409 no_sequence_ref means the campaign has no attached sequence. Throws a coded error.
export async function updateCampaignPrompt(id, { key, subject, body, model, dayOffset, threadMode, researchConfig }) {
  const headers = await authHeader();
  try {
    const payload = { key };
    if (subject !== undefined) payload.subject = subject;
    if (body !== undefined) payload.body = body;
    if (model) payload.model = model;
    if (dayOffset !== undefined) payload.dayOffset = dayOffset;
    if (threadMode !== undefined) payload.threadMode = threadMode;
    if (researchConfig !== undefined) payload.researchConfig = researchConfig;
    const res = await axios.patch(
      `${T6_BASE}/api/gtm/campaigns/${id}/prompts`,
      payload,
      { headers }
    );
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// POST /api/gtm/sequences/:sequenceId/steps/:stepKey/revise — AI-revise a step's template
// per { instruction, model } while PRESERVING every [bracketed] shortcode. PREVIEW ONLY (no
// save). Resolves { saved:false, preview:{ subject, body }, tokensPreserved, retried }. A 422
// { error:'shortcodes_violated', missing, invented } surfaces coded (err.code/err.data); a
// 424 anthropic_key_missing when the key is unset. Throws a coded gtmActionError on failure.
export async function reviseStepTemplate(sequenceId, stepKey, { instruction, model }) {
  const headers = await authHeader();
  try {
    const res = await axios.post(
      `${T6_BASE}/api/gtm/sequences/${sequenceId}/steps/${encodeURIComponent(stepKey)}/revise`,
      { instruction, model },
      { headers }
    );
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// ─── GTM system prompts: versioned, rollback-able (compose_opener, fit_check, ...) ──────
// The live prompt docs the pipeline reads. Listing uses useGtmResource('/api/gtm/prompts').

// GET /api/gtm/prompts/:key/versions. Returns the version history rows [{ version, body,
// savedBy, savedAt }], newest first (read-only, for the rollback panel). Tolerates a bare array.
export async function fetchPromptVersions(key) {
  const headers = await authHeader();
  try {
    const res = await axios.get(`${T6_BASE}/api/gtm/prompts/${encodeURIComponent(key)}/versions`, { headers });
    const d = res.data || {};
    return Array.isArray(d.versions) ? d.versions : Array.isArray(d) ? d : [];
  } catch (err) {
    throw gtmActionError(err);
  }
}

// PATCH /api/gtm/prompts/:key { body, name?, description? }. Saves a NEW version (bumps the
// version number, appends a history row, updates the live doc). Resolves the updated prompt
// doc. Throws a coded gtmActionError on failure.
export async function savePrompt(key, patch) {
  const headers = await authHeader();
  try {
    const res = await axios.patch(`${T6_BASE}/api/gtm/prompts/${encodeURIComponent(key)}`, patch || {}, { headers });
    return (res.data && res.data.prompt) || res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// POST /api/gtm/prompts/:key/rollback { version }. Restores a prior version as the NEW live
// version (never destructive; the current version stays in history). Resolves the new live doc.
export async function rollbackPrompt(key, version) {
  const headers = await authHeader();
  try {
    const res = await axios.post(`${T6_BASE}/api/gtm/prompts/${encodeURIComponent(key)}/rollback`, { version }, { headers });
    return (res.data && res.data.prompt) || res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// POST /api/gtm/campaigns/:id/prompts/add-step — append a new follow-up step (the backend
// seeds sensible defaults + a starter prompt). Optional body { label, prompt, model,
// dayOffset, threadMode, researchConfig }. Resolves the full { draftPrompts: [...] } array.
export async function addCampaignPromptStep(id, body) {
  const headers = await authHeader();
  try {
    const res = await axios.post(`${T6_BASE}/api/gtm/campaigns/${id}/prompts/add-step`, body || {}, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// DELETE /api/gtm/campaigns/:id/prompts/:key — remove a follow-up step. Step 1 (the opener)
// is undeletable (backend 409 cannot_delete_step_1). Resolves the full { draftPrompts: [...] }.
export async function deleteCampaignPromptStep(id, key) {
  const headers = await authHeader();
  try {
    const res = await axios.delete(`${T6_BASE}/api/gtm/campaigns/${id}/prompts/${encodeURIComponent(key)}`, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// POST /api/gtm/campaigns/:id/regenerate-drafts — recompose drafts for contacts in
// this campaign that are STILL awaiting approval (pending_approval); already-approved
// or sent contacts are untouched. Human-triggered behind a confirm. Returns whatever
// the server reports (e.g. { ok, regenerated }).
export async function regenerateCampaignDrafts(id) {
  const headers = await authHeader();
  try {
    const res = await axios.post(`${T6_BASE}/api/gtm/campaigns/${id}/regenerate-drafts`, {}, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// GET /api/gtm/instantly/campaigns — the admin's Instantly campaign list for the wiring
// picker: [{ id, name, status }] (status is an Instantly numeric code). Tolerates a
// { campaigns:[...] } envelope. A 424 instantly_key_missing (key unset) throws a coded
// gtmActionError (err.httpStatus === 424, err.code === 'instantly_key_missing') so the
// caller can fall back to manual id entry rather than blanking the field.
export async function fetchInstantlyCampaigns() {
  const headers = await authHeader();
  try {
    const res = await axios.get(`${T6_BASE}/api/gtm/instantly/campaigns`, { headers });
    const d = res.data;
    return Array.isArray(d) ? d : Array.isArray(d && d.campaigns) ? d.campaigns : [];
  } catch (err) {
    throw gtmActionError(err);
  }
}

// PATCH /api/gtm/campaigns/:id/instantly-campaign — wire a GTM campaign to an Instantly
// campaign. Body { instantlyCampaignId } (UUID-shaped; the backend validates). Resolves
// res.data on success; throws a coded gtmActionError (e.g. instantly_campaign_id_invalid)
// so the caller renders it inline.
export async function wireInstantlyCampaign(id, instantlyCampaignId) {
  const headers = await authHeader();
  try {
    const res = await axios.patch(
      `${T6_BASE}/api/gtm/campaigns/${id}/instantly-campaign`,
      { instantlyCampaignId },
      { headers }
    );
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// UNLINK: clear a campaign's Instantly link by PATCHing the same endpoint with an empty id.
// The current backend validator REQUIRES a non-empty UUID, so this returns
// instantly_campaign_id_required (400) TODAY — surfaced verbatim to the caller, never faked.
// It needs a one-line T6 change (accept '' / null to clear, or a DELETE route); wiring it the
// same way as the linker means it starts working the moment T6 allows clearing.
export async function unlinkInstantlyCampaign(id) {
  const headers = await authHeader();
  try {
    const res = await axios.patch(
      `${T6_BASE}/api/gtm/campaigns/${id}/instantly-campaign`,
      { instantlyCampaignId: '' },
      { headers }
    );
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// POST /api/gtm/instantly/campaigns/:id/provision — set the Instantly sequence steps from
// the wired GTM campaign's prompts, apply a weekday schedule, and activate the campaign on
// Instantly. `id` is the Instantly campaign id. 424 instantly_key_missing when the key is
// unset. Resolves res.data on success; throws a coded gtmActionError otherwise.
export async function provisionInstantlyCampaign(instantlyCampaignId) {
  const headers = await authHeader();
  try {
    const res = await axios.post(
      `${T6_BASE}/api/gtm/instantly/campaigns/${instantlyCampaignId}/provision`,
      {},
      { headers }
    );
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// POST /api/gtm/instantly/sync-campaigns — pull the latest campaign list + live status from
// Instantly into the server store, then the list read reflects it. No body. Returns whatever
// the server reports (e.g. { ok, synced }). Mirrors the Mailboxes sync-now idiom. ASSUMED
// endpoint until T6 ships it: a 404/501 surfaces as a readable error (the section fails soft),
// never a silent success. Coded errors ride gtmActionError.
export async function syncInstantlyCampaigns() {
  const headers = await authHeader();
  try {
    const res = await axios.post(`${T6_BASE}/api/gtm/instantly/sync-campaigns`, {}, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// GTM-REACH-01. POST /api/gtm/analytics/sync-daily, body { days, full }. Pulls Instantly's
// daily campaign and account analytics into gtm_daily_stats now, so a workspace with nothing
// synced can fill its own history from the UI instead of waiting for a scheduled job.
// Backed by gtmController.syncDailyStatsNow (gtmRoutes.js:332). A missing Instantly key comes
// back 424 with code instantly_key_missing and a rate limit comes back 429; both ride
// gtmActionError so the caller can say which one happened rather than "sync failed".
export async function syncDailyStats({ days, full } = {}) {
  const headers = await authHeader();
  try {
    const body = {};
    if (days != null) body.days = days;
    if (full) body.full = true;
    const res = await axios.post(`${T6_BASE}/api/gtm/analytics/sync-daily`, body, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// POST /api/gtm/instantly/campaigns/:id/activate — set an Instantly campaign live. No body.
// `id` is the Instantly campaign id. On failure the server's plain reason (e.g. "no mailboxes
// attached") surfaces verbatim via gtmActionError so the card can render it. ASSUMED endpoint
// until T6 ships it (a 404/501 is surfaced, never a silent success).
export async function activateInstantlyCampaign(id) {
  const headers = await authHeader();
  try {
    const res = await axios.post(`${T6_BASE}/api/gtm/instantly/campaigns/${encodeURIComponent(id)}/activate`, {}, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// POST /api/gtm/instantly/campaigns/:id/pause — pause a live Instantly campaign. No body.
// Failure reason surfaces verbatim via gtmActionError. ASSUMED endpoint until T6 ships it.
export async function pauseInstantlyCampaign(id) {
  const headers = await authHeader();
  try {
    const res = await axios.post(`${T6_BASE}/api/gtm/instantly/campaigns/${encodeURIComponent(id)}/pause`, {}, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// ─── Sequences editor + preview (Chunk D) ───────────────────────────────────

// PATCH /api/gtm/sequences/:id — edit copy/structure (no deploy). Body may include
// { name, audienceHint, aliases, status, linkAssetKey, steps }. Resolves
// { ok, sequence, tokenWarnings:[{step, removedTokens[]}] }. A 422 invalid_step
// throws with err.code='invalid_step' and err.data.step naming the bad step.
export async function updateSequence(id, patch) {
  const headers = await authHeader();
  try {
    const res = await axios.patch(`${T6_BASE}/api/gtm/sequences/${id}`, patch, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// POST /api/gtm/sequences/:id/preview — assemble every step for a sample contact.
// Body { sampleContact:{ name, company, title, evidence? }, draftSteps? }. When
// draftSteps (the in-flight, unsaved editor steps) is passed, the backend composes
// against those instead of the saved sequence, so the preview reflects unsaved edits
// (e.g. per-step stepDirection). Resolves
// { ok, key, variant, draft, openerUsed, composeNote, steps:[{...,assembledBody, linkError?}] }.
export async function previewSequence(id, sampleContact, draftSteps) {
  const headers = await authHeader();
  try {
    const body = { sampleContact };
    if (Array.isArray(draftSteps)) body.draftSteps = draftSteps;
    const res = await axios.post(`${T6_BASE}/api/gtm/sequences/${id}/preview`, body, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// POST /api/gtm/sequences — create a new DRAFT sequence from a name + audience hint.
// The backend seeds a minimal step-1 draft; returns the created doc (in the same
// shape the editor consumes) so the UI can open it straight in the editor. Reads
// res.data.sequence, falling back to res.data.
export async function createSequence(payload) {
  const headers = await authHeader();
  try {
    const res = await axios.post(`${T6_BASE}/api/gtm/sequences`, payload, { headers });
    return (res.data && res.data.sequence) || res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// POST /api/gtm/sequences/route-preview — which live sequence a given audience hint
// routes to, and why. Body { audienceHint }. Returns exactly { matchedKey,
// matchedName, confidence, reasoning } — matchedName is the "Routes to:" badge and
// reasoning the short why; the caller keeps a defensive read for older shapes.
// Read-only: makes the automatic send-time routing visible while composing a request.
export async function routePreviewSequence(audienceHint) {
  const headers = await authHeader();
  try {
    const res = await axios.post(`${T6_BASE}/api/gtm/sequences/route-preview`, { audienceHint }, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// ─── Link assets: writes (Chunk C) ──────────────────────────────────────────

// POST /api/gtm/link-assets — register an existing URL. Body { key, label, url, type?, isDefault? }.
export async function createLinkAsset(payload) {
  const headers = await authHeader();
  try {
    const res = await axios.post(`${T6_BASE}/api/gtm/link-assets`, payload, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// POST /api/gtm/link-assets/upload — multipart upload to S3. `form` is a FormData
// with `file` + { label, key?, type?, isDefault? }. onProgress(0..100) drives a bar.
export async function uploadLinkAsset(form, onProgress) {
  const headers = await authHeader();
  try {
    const res = await axios.post(`${T6_BASE}/api/gtm/link-assets/upload`, form, {
      headers, // let axios set the multipart boundary; do not force Content-Type
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
      },
    });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// PUT /api/gtm/link-assets/:id/file — overwrite the hosted file in place; the URL
// never changes. `form` is a FormData with `file`. Resolves { ok, linkAsset, cache }
// where cache = { invalidated, reason? } (CloudFront refresh). GTM-LINK-ASSET-REPLACE-UI-01.
export async function replaceLinkAssetFile(id, form, onProgress) {
  const headers = await authHeader();
  try {
    const res = await axios.put(`${T6_BASE}/api/gtm/link-assets/${id}/file`, form, {
      headers, // let axios set the multipart boundary; do not force Content-Type
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
      },
    });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// PATCH /api/gtm/link-assets/:id — partial update { label?, url?, type?, isDefault?, key? }.
export async function updateLinkAsset(id, patch) {
  const headers = await authHeader();
  try {
    const res = await axios.patch(`${T6_BASE}/api/gtm/link-assets/${id}`, patch, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// DELETE /api/gtm/link-assets/:id.
export async function deleteLinkAsset(id) {
  const headers = await authHeader();
  try {
    const res = await axios.delete(`${T6_BASE}/api/gtm/link-assets/${id}`, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// ─── Suppressions (Chunk H) ──────────────────────────────────────────────────
// NOTE: the backend exposes POST + DELETE only (no GET list), so the manager shows
// session-added rows optimistically until a list route exists.

// POST /api/gtm/suppressions — { email? , domain?, note? } (at least one of email/domain).
export async function createSuppression(payload) {
  const headers = await authHeader();
  try {
    const res = await axios.post(`${T6_BASE}/api/gtm/suppressions`, payload, { headers });
    return res.data; // { ok, suppression }
  } catch (err) {
    throw gtmActionError(err);
  }
}

// DELETE /api/gtm/suppressions/:id.
export async function deleteSuppression(id) {
  const headers = await authHeader();
  try {
    const res = await axios.delete(`${T6_BASE}/api/gtm/suppressions/${id}`, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// PATCH /api/gtm/suppressions/:id — update a suppression in place (set domain + note).
// Used to complete a missing company domain on the original row.
export async function patchSuppression(id, patch) {
  const headers = await authHeader();
  try {
    const res = await axios.patch(`${T6_BASE}/api/gtm/suppressions/${id}`, patch, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// ─── Competitors (manual competitor-protection backstop; mirrors suppressions) ───────────
// List via useGtmResource('/api/gtm/competitors', (d) => d.competitors). A row: { id, company,
// domain, competesWith[], mode:'block_capability'|'block_entirely', note, addedBy, createdAt }.

// POST /api/gtm/competitors { company, domain, competesWith[], mode?, note? }. domain is
// required; block_capability requires >=1 competesWith family. Upsert by domain. Coded errors:
// domain_required, competes_with_required. Returns { ok, competitor }.
export async function createCompetitor(payload) {
  const headers = await authHeader();
  try {
    const res = await axios.post(`${T6_BASE}/api/gtm/competitors`, payload, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// PATCH /api/gtm/competitors/:id { company?, domain?, competesWith?, mode?, note? }.
export async function updateCompetitor(id, patch) {
  const headers = await authHeader();
  try {
    const res = await axios.patch(`${T6_BASE}/api/gtm/competitors/${id}`, patch, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// DELETE /api/gtm/competitors/:id.
export async function deleteCompetitor(id) {
  const headers = await authHeader();
  try {
    const res = await axios.delete(`${T6_BASE}/api/gtm/competitors/${id}`, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// ─── Mailboxes / capacity (Instantly is the source of truth) ─────────────────────────────
// The capacity surface reads via useGtmResource('/api/gtm/capacity'): a summary
// { mailboxes:[{ email, domain, warmupStatus, dailyLimit, lastSyncedAt }],
//   dailySendCapacity, dailyEnrichBudget, capacitySource }. Mailboxes are never added
// here; they appear on sync. POST below pulls the latest from Instantly and upserts.

// POST /api/gtm/mailboxes/sync-now — pull mailboxes from Instantly and upsert them.
// No body. Returns { ok, upserted, ... }. Coded errors ride on gtmActionError.
export async function syncMailboxesNow() {
  const headers = await authHeader();
  try {
    const res = await axios.post(`${T6_BASE}/api/gtm/mailboxes/sync-now`, {}, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// PUT /api/gtm/mailboxes/autopilot — flip the mailbox-autopilot master switch. Body
// { enabled: boolean }. When on, United reviews each mailbox's health hourly and nudges its
// daily send up or down (or pauses it) within fixed safety limits; pinned mailboxes are never
// touched. A backend env kill switch can force autopilot off, in which case this PUT is refused
// server-side and the control renders disabled. ASSUMED endpoint until the autopilot backend
// ships: a 404/501 surfaces verbatim via gtmActionError so the control reverts its optimistic
// state and never shows a false success (Rule 3). Returns res.data.
export async function setMailboxAutopilot(enabled) {
  const headers = await authHeader();
  try {
    const res = await axios.put(`${T6_BASE}/api/gtm/mailboxes/autopilot`, { enabled: !!enabled }, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// PUT /api/gtm/mailboxes/:email/pin — pin ("manual control") or unpin a single mailbox. Body
// { pinned: boolean }. A pinned mailbox is excluded from every autopilot adjustment; its daily
// limit stays exactly where a human set it. The email is path-encoded. ASSUMED endpoint until
// the autopilot backend ships: a failure surfaces verbatim so the pin toggle reverts and never
// shows a false success. Returns res.data.
export async function setMailboxPin(email, pinned) {
  const headers = await authHeader();
  try {
    const res = await axios.put(`${T6_BASE}/api/gtm/mailboxes/${encodeURIComponent(email)}/pin`, { pinned: !!pinned }, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// POST /api/gtm/fleet/plan — build a fleet-growth plan for a target. Body { target, mode }
// where mode is 'mailboxes' or 'leads'. Returns the plan: domains + boxes, pre-warmed vs regular
// mix, a LIVE simulated one-time quote (with an orderId to confirm), and the new monthly total
// including any Instantly tier step. ASSUMED endpoint until the fleet-scaler backend ships: a
// 404/501 surfaces verbatim via gtmActionError and the card fails soft. Returns res.data.
export async function fetchFleetPlan(target, mode) {
  const headers = await authHeader();
  try {
    const res = await axios.post(`${T6_BASE}/api/gtm/fleet/plan`, { target, mode }, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// POST /api/gtm/orders/:id/confirm — confirm and charge a simulated fleet order. The card on file
// with Instantly is charged server-side; this cannot be undone from United. A refusal (ceiling 0,
// price moved, plan/scope) comes back coded (err.code / err.message) so the caller can render the
// reason in plain language. ASSUMED endpoint until the fleet-scaler backend ships. Returns res.data.
export async function confirmFleetOrder(orderId) {
  const headers = await authHeader();
  try {
    const res = await axios.post(`${T6_BASE}/api/gtm/orders/${encodeURIComponent(orderId)}/confirm`, {}, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// ─── Verify lanes (list verification via Scrubby) ───────────────────────────
// POST /api/gtm/verify/upload — multipart { file, mode } where mode is 'all' (verify every
// address) or 'catchall' (deep-verify only, file is already catch-alls). NO spend: this analyzes
// the file and returns { jobId, columns, columnMapping, emailColumn, preflight:{ rowsFound,
// duplicatesRemoved, alreadyKnown, suppressed, competitorBlocked, needChecking, estimatedCredits },
// mode }. ASSUMED endpoint until the verify backend ships; a 404/501 surfaces verbatim and the
// surface fails soft. Returns res.data.
export async function verifyUpload(form) {
  const headers = await authHeader();
  try {
    const res = await axios.post(`${T6_BASE}/api/gtm/verify/upload`, form, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// POST /api/gtm/verify/:jobId/mapping — correct the detected email column before any spend. Body
// { emailColumn }. Returns the recomputed { columnMapping, emailColumn, preflight }. Still no spend.
export async function verifyRemapEmail(jobId, emailColumn) {
  const headers = await authHeader();
  try {
    const res = await axios.post(`${T6_BASE}/api/gtm/verify/${encodeURIComponent(jobId)}/mapping`, { emailColumn }, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// POST /api/gtm/verify/:jobId/start — SPENDS credits and begins verification. Returns the job
// { jobId, status, progress, results, expectedReadyAt }. A refusal (insufficient credits, price
// moved) comes back coded so the caller can render it in plain language. Returns res.data.
export async function startVerification(jobId) {
  const headers = await authHeader();
  try {
    const res = await axios.post(`${T6_BASE}/api/gtm/verify/${encodeURIComponent(jobId)}/start`, {}, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// GET /api/gtm/verify/jobs/:jobId — poll one job's status/results while it runs. Returns the job.
export async function fetchVerifyJob(jobId) {
  const headers = await authHeader();
  try {
    const res = await axios.get(`${T6_BASE}/api/gtm/verify/jobs/${encodeURIComponent(jobId)}`, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// POST /api/gtm/verify/:jobId/promote — add this job's VALID contacts to the pipeline. They enter
// the normal approval flow and never send without review; pending catch-alls promote later on
// their own when their verdict lands. Returns res.data { promoted, ... }.
export async function promoteVerifiedContacts(jobId) {
  const headers = await authHeader();
  try {
    const res = await axios.post(`${T6_BASE}/api/gtm/verify/${encodeURIComponent(jobId)}/promote`, {}, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// POST /api/gtm/verify/credits/refresh — re-read the Scrubby balance. This costs 1 credit (stated
// on the button). Returns the fresh { balance, asOf, estimatedDeepChecks }. Throws coded on refusal.
export async function refreshVerifyCredits() {
  const headers = await authHeader();
  try {
    const res = await axios.post(`${T6_BASE}/api/gtm/verify/credits/refresh`, {}, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// GET /api/gtm/verify/jobs/:jobId/download — the cleaned CSV as a Blob (authenticated, so it cannot
// be a plain link). The caller turns it into an object URL and triggers a download. Throws coded.
export async function downloadVerifyCsv(jobId) {
  const headers = await authHeader();
  try {
    const res = await axios.get(`${T6_BASE}/api/gtm/verify/jobs/${encodeURIComponent(jobId)}/download`, { headers, responseType: 'blob' });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// PUT /api/gtm/rotation/order — persist a new persona (pitch) rotation order. Body
// { order: [personaKey, ...] } is the FULL ordered array of persona keys. Locked
// positions (completed + the active pitch) are enforced server-side; a validation error
// (e.g. locked_position_moved) comes back coded so the caller can render it verbatim.
// ASSUMED endpoint until the rotation-order backend ships: a 404/501 surfaces as a
// readable error (the panel fails soft), never a silent success. Returns res.data.
export async function putRotationOrder(order) {
  const headers = await authHeader();
  try {
    const res = await axios.put(`${T6_BASE}/api/gtm/rotation/order`, { order }, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// POST /api/gtm/credit-pool — re-base the Apollo credit pool to a true balance
// (admin only; floor-guarded server-side; emits the credit_pool SSE so the header
// chip updates live). Body { balance:<number> }. Returns { ok, pool }.
export async function setCreditPool(balance) {
  const headers = await authHeader();
  try {
    const res = await axios.post(`${T6_BASE}/api/gtm/credit-pool`, { balance }, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// POST /api/gtm/controls — update the gtm_controls singleton (admin). Body is a partial patch;
// the master send switch sends { sendingEnabled: boolean }. Resolves the updated controls doc.
// A failure (e.g. the field not yet accepted server-side) surfaces verbatim via gtmActionError
// so the caller can revert its optimistic state and never shows a false success (Rule 3).
export async function setGtmControls(patch) {
  const headers = await authHeader();
  try {
    const res = await axios.post(`${T6_BASE}/api/gtm/controls`, patch || {}, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// ─── Proofs (the inventory of what we can claim) ─────────────────────────────
// GET /api/gtm/proofs is live: { proofs:[{ _id, whatWeDo, client, clientDomain, dimension,
// evidenceQuote, sourceTranscriptId, builtAt }] }. The intel/proposal/demand-signal/write
// endpoints below are ASSUMED (not all shipped yet); each fails soft with a verbatim error so
// the Proofs tab renders what exists and never fakes the rest.

// POST /api/gtm/proofs/mine-now — mine "things we do for clients" from the transcript library.
// No body. Returns { transcriptsScanned, proofsUpserted, byDimension }. Live today.
export async function mineProofsNow() {
  const headers = await authHeader();
  try {
    const res = await axios.post(`${T6_BASE}/api/gtm/proofs/mine-now`, {}, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// POST /api/gtm/proofs/extract { transcriptId } — extract proofs from ONE transcript (ASSUMED;
// scoped counterpart of mine-now). Surfaces a verbatim error until T6 ships the scoped route.
export async function extractProofsFromTranscript(transcriptId) {
  const headers = await authHeader();
  try {
    const res = await axios.post(`${T6_BASE}/api/gtm/proofs/extract`, { transcriptId }, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// Create/edit a proof (ASSUMED write path). PATCH /api/gtm/proofs/:id when id is present, else
// POST /api/gtm/proofs. Body { whatWeDo, client, evidenceQuote, dimension, aliases }.
export async function saveProof({ id, ...patch } = {}) {
  const headers = await authHeader();
  try {
    const res = id
      ? await axios.patch(`${T6_BASE}/api/gtm/proofs/${encodeURIComponent(id)}`, patch, { headers })
      : await axios.post(`${T6_BASE}/api/gtm/proofs`, patch, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// POST /api/gtm/proofs/:id/retire — retire a proof (ASSUMED write path).
export async function retireProof(id) {
  const headers = await authHeader();
  try {
    const res = await axios.post(`${T6_BASE}/api/gtm/proofs/${encodeURIComponent(id)}/retire`, {}, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// POST /api/gtm/proofs/dedupe-now — run dedup across proofs (INTEL-03). No body. Returns merge
// counts (e.g. { merged, groups, scanned }). Surfaces a verbatim error on failure.
export async function dedupeProofsNow() {
  const headers = await authHeader();
  try {
    const res = await axios.post(`${T6_BASE}/api/gtm/proofs/dedupe-now`, {}, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// useGtmAdmin — is the signed-in user a united_ai admin? Mirrors the backend
// requireStaffAccess check: an active productAccess grant { product:'united_ai',
// role:'admin', revokedAt:null } on the Employee doc (GET /api/employee/:uid, the
// same endpoint Login uses; it returns the full doc). Fails closed: false until
// confirmed, so non-admins (and any lookup failure) see read-only UI. The backend
// POST /credit-pool is the true enforcement; this only gates the editor affordance.
//
// Subscribes to onAuthStateChanged rather than reading currentUser once at mount:
// on a hard refresh Firebase restores the session asynchronously, so the check
// must run WHEN auth resolves, not once at mount (which would leave isAdmin stuck
// at false). The listener fires immediately with the current user (null if not
// yet restored) and again when the session restores or changes.
export function useGtmAdmin() {
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    let unmounted = false;
    let reqId = 0; // guards against out-of-order responses across auth changes

    const check = async (user) => {
      const myReq = ++reqId;
      if (!user) {
        if (!unmounted) setIsAdmin(false); // signed out / not yet restored -> read-only
        return;
      }
      try {
        const res = await axios.get(`${T6_BASE}/api/employee/${user.uid}`);
        const grants = Array.isArray(res.data && res.data.productAccess) ? res.data.productAccess : [];
        const ok = grants.some(
          (g) => g && g.product === 'united_ai' && g.role === 'admin' && g.revokedAt == null
        );
        if (!unmounted && myReq === reqId) setIsAdmin(ok);
      } catch {
        if (!unmounted && myReq === reqId) setIsAdmin(false); // fail closed
      }
    };

    const unsubscribe = onAuthStateChanged(getAuth(), (user) => {
      check(user);
    });

    return () => {
      unmounted = true;
      unsubscribe();
    };
  }, []);
  return isAdmin;
}

// ─── GTM Inbox (Chunk 4) ─────────────────────────────────────────────────────

// POST /api/gtm/contacts/:id/reply/approve — send the (optionally edited) reply
// draft via Instantly. The ONLY reply-send path, human-triggered. Coded errors:
// no_draft (409), send_disabled (424), reply_thread_unknown (409), reply_send_failed (502).
export async function approveReply(id, draft) {
  const headers = await authHeader();
  try {
    const res = await axios.post(
      `${T6_BASE}/api/gtm/contacts/${id}/reply/approve`,
      draft != null ? { draft } : {},
      { headers }
    );
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// POST /api/gtm/contacts/:id/reply/reject — discard a reply draft (do not send).
// ASSUMED endpoint (a reply_rejected event exists in the model but no route yet);
// degrades to a surfaced error until the backend ships it.
export async function rejectReply(id, reason) {
  const headers = await authHeader();
  try {
    const res = await axios.post(
      `${T6_BASE}/api/gtm/contacts/${id}/reply/reject`,
      reason ? { reason } : {},
      { headers }
    );
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// POST /api/gtm/contacts/:id/reply/feedback — write a feedback_submitted learning
// log event { rating, note, correctedIntent }. ASSUMED endpoint (the model + event
// type exist but no write route yet); degrades to a surfaced error until it ships.
export async function submitReplyFeedback(id, feedback) {
  const headers = await authHeader();
  try {
    const res = await axios.post(
      `${T6_BASE}/api/gtm/contacts/${id}/reply/feedback`,
      feedback || {},
      { headers }
    );
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// useReplies — the Inbox feed: every inbound reply, matched to a GTM contact or not,
// paginated (no all-messages fetch). GET /api/gtm/replies -> { replies, total,
// unreadTotal, limit, offset, unreadOnly }; tolerates a bare array. Exposes local
// mutators so the page can update optimistically and roll back on failure.
//
// REPLY-READ-01: read state is SERVER-side and shared across admins. Every row carries
// `replyId` (present even when `id` is null for an unmatched reply), `read`, `readAt`,
// and `readBy`; the response carries `unreadTotal`, which counts rows this client has
// never fetched. `?unread=true` narrows the page itself so paging never walks read rows.
//
// REPLY-NOISE-01: `?kind=genuine|noise|all` hides out-of-office / bounce / unsubscribe
// traffic, judged by the Haiku classifier (`mined.type`), NOT by Instantly's `category`
// field — that one is an interest score and is blank on every confirmed auto-reply.
// The filter is fail-open: kind=genuine excludes CONFIRMED noise, so a reply the
// classifier has not reached yet still shows. Do not narrow it to a positive verdict.
// The tab defaults to `genuine`; the badge tracks `unreadGenuineTotal`.
export function useReplies(pageSize = 20) {
  const [replies, setReplies] = useState([]);
  const [threads, setThreads] = useState([]);
  const [total, setTotal] = useState(null);          // conversations, in groupBy=thread mode
  const [totalMessages, setTotalMessages] = useState(null);
  const [truncated, setTruncated] = useState(false);
  const [unreadTotal, setUnreadTotal] = useState(null);
  const [unreadGenuineTotal, setUnreadGenuineTotal] = useState(null);
  const [counts, setCounts] = useState(null); // { all, genuine, noise, unclassified }
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [kind, setKind] = useState('genuine');
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(
    async (nextOffset, append, unread, kindParam) => {
      setLoading(true);
      setError(null);
      try {
        const headers = await authHeader();
        // REPLY-THREAD-01: the CONVERSATION is the unit of pagination, so Load more can
        // never cut an exchange in half. `total` counts conversations in this mode and
        // `totalMessages` counts messages; every unread count stays message-level, so
        // the badge means the same thing either way.
        const params = { limit: pageSize, offset: nextOffset, groupBy: 'thread' };
        if (unread) params.unread = 'true';
        if (kindParam) params.kind = kindParam;
        const res = await axios.get(`${T6_BASE}/api/gtm/replies`, { headers, params });
        const d = res.data || {};
        const rows = Array.isArray(d.replies) ? d.replies : Array.isArray(d) ? d : [];
        const ths = Array.isArray(d.threads) ? d.threads : [];
        setReplies((prev) => (append ? [...prev, ...rows] : rows));
        setThreads((prev) => (append ? [...prev, ...ths] : ths));
        setTotal(d.total != null ? d.total : null);
        setTotalMessages(d.totalMessages != null ? d.totalMessages : null);
        setTruncated(!!d.truncated);
        if (d.unreadTotal != null) setUnreadTotal(d.unreadTotal);
        if (d.unreadGenuineTotal != null) setUnreadGenuineTotal(d.unreadGenuineTotal);
        if (d.counts) setCounts(d.counts);
        setOffset(nextOffset);
      } catch (err) {
        setError(readError(err));
        if (!append) { setReplies([]); setThreads([]); }
      } finally {
        setLoading(false);
      }
    },
    [pageSize]
  );

  useEffect(() => {
    load(0, false, unreadOnly, kind);
  }, [load, unreadOnly, kind]);

  const reload = useCallback(() => load(0, false, unreadOnly, kind), [load, unreadOnly, kind]);
  const loadMore = useCallback(
    () => load(offset + pageSize, true, unreadOnly, kind),
    [load, offset, pageSize, unreadOnly, kind]
  );

  // Optimistic-update helpers (approve/reject remove; failure restores). `wasUnread`
  // keeps the badge exact when an unread reply is approved straight out of the list.
  const removeReply = useCallback((id, wasUnread) => {
    setReplies((prev) => prev.filter((r) => r.id !== id));
    setTotal((t) => (t != null ? Math.max(0, t - 1) : t));
    if (wasUnread) {
      setUnreadTotal((u) => (u != null ? Math.max(0, u - 1) : u));
      // Only a genuine reply can be approved/rejected, so it also leaves the badge.
      setUnreadGenuineTotal((u) => (u != null ? Math.max(0, u - 1) : u));
    }
  }, []);
  const restoreReply = useCallback((reply) => {
    if (!reply) return;
    setReplies((prev) => (prev.some((r) => r.id === reply.id) ? prev : [reply, ...prev]));
    setTotal((t) => (t != null ? t + 1 : t));
    if (reply.read === false) {
      setUnreadTotal((u) => (u != null ? u + 1 : u));
      setUnreadGenuineTotal((u) => (u != null ? u + 1 : u));
    }
  }, []);

  // Flip read on loaded rows. The badge itself only ever moves to a server-reported
  // unreadTotal, so it can never drift from what the server actually holds.
  const applyRead = useCallback((replyIds, read) => {
    const s = new Set(replyIds || []);
    if (!s.size) return;
    setReplies((prev) => prev.map((r) => (s.has(r.replyId) ? { ...r, read: !!read } : r)));
  }, []);
  const applyReadAll = useCallback((read) => {
    setReplies((prev) => prev.map((r) => ({ ...r, read: !!read })));
  }, []);

  // Server totals, from a read-mutation response. Always take both together so the
  // badge and the "N hidden" line can never disagree.
  const applyTotals = useCallback((d) => {
    if (!d) return;
    if (d.unreadTotal != null) setUnreadTotal(d.unreadTotal);
    if (d.unreadGenuineTotal != null) setUnreadGenuineTotal(d.unreadGenuineTotal);
  }, []);

  return {
    replies, threads, total, totalMessages, truncated,
    unreadTotal, unreadGenuineTotal, counts, offset, pageSize, loading, error,
    unreadOnly, setUnreadOnly, kind, setKind,
    reload, loadMore, removeReply, restoreReply,
    applyRead, applyReadAll, applyTotals,
  };
}

// GET /api/gtm/replies/thread/:threadId — the whole conversation, OLDEST FIRST, and
// unlike the inbox list it INCLUDES our own outbound messages (every row carries
// `direction` and `outbound`). A thread with our side stripped out reads as half a
// conversation, so the pane shows both.
//
// canReply / blockedReason are the composer's contract: when canReply is false the box
// is disabled with the real reason rather than letting someone type a reply that gets
// refused on submit. replyToReplyId is the id to POST to, always the newest INBOUND
// message; never pick one of ours.
export async function fetchReplyThread(threadId) {
  const headers = await authHeader();
  try {
    const res = await axios.get(`${T6_BASE}/api/gtm/replies/thread/${encodeURIComponent(threadId)}`, { headers });
    return res.data || {};
  } catch (err) {
    throw gtmActionError(err);
  }
}

// POST /api/gtm/replies/:replyId/reply  { body, subject? } — the HUMAN reply path.
// Keyed on the reply row, not a contact id, which is what makes it work at all: the
// older contact-keyed approve path can only reach replies matched to a gtm_contact, and
// almost none are. Sends from the alias the prospect wrote to, passed explicitly.
// Returns { ok, sent, threadId, replyFromMailbox, mailboxSource, mailboxInFleet,
// subject, storedInThread }.
//
// storedInThread:false means the reply WAS SENT and only our local copy failed to save.
// Never surface that as a send failure.
export async function sendThreadReply(replyId, { body, subject } = {}) {
  const headers = await authHeader();
  const payload = { body: body || '' };
  if (subject && subject.trim()) payload.subject = subject.trim();
  try {
    const res = await axios.post(`${T6_BASE}/api/gtm/replies/${replyId}/reply`, payload, { headers });
    return res.data || {};
  } catch (err) {
    throw gtmActionError(err);
  }
}

// PATCH /api/gtm/replies/:id/read  { read } — mark ONE reply read (default) or unread.
// :id is the replyId (gtm_replies _id), which every row carries, matched or not.
// Returns { ok, replyId, read, readAt, readBy, unreadTotal, unreadGenuineTotal }.
export async function setReplyRead(replyId, read = true) {
  const headers = await authHeader();
  try {
    const res = await axios.patch(`${T6_BASE}/api/gtm/replies/${replyId}/read`, { read: !!read }, { headers });
    return res.data || {};
  } catch (err) {
    throw gtmActionError(err);
  }
}

// POST /api/gtm/replies/read  { read, replyIds? , all?, kind? } — the bulk form. Use
// replyIds to land a screenful of auto-marked rows in ONE request, and all:true for
// "mark all read", which has to cover rows this client never fetched or the badge
// stays hot. ALWAYS pass `kind` with an `all` sweep so it narrows to the set the tab
// is actually showing; without it, "mark all read" silently clears the hidden
// out-of-office rows too. Returns { ok, read, all, kind, requested, modified,
// skipped, unreadTotal, unreadGenuineTotal }.
export async function bulkReplyRead({ replyIds, all, kind, read = true } = {}) {
  const headers = await authHeader();
  const body = { read: !!read };
  if (all) body.all = true;
  else body.replyIds = replyIds || [];
  if (kind === 'genuine' || kind === 'noise') body.kind = kind;
  try {
    const res = await axios.post(`${T6_BASE}/api/gtm/replies/read`, body, { headers });
    return res.data || {};
  } catch (err) {
    throw gtmActionError(err);
  }
}

// ─── Generic contact import (file preview/import + saved Apollo lists) ────────

// POST /api/gtm/contacts/preview — server-side parse of an uploaded CSV/XLSX
// (campaign-agnostic; XLSX handled server-side, unlike the client-side CSV path).
// `form` is a FormData with `file`. This is a SELF-CONTAINED parser: it returns
// res.data { rows|preview|sample, total, columnMapping, missingFields, note? } and
// NO uploadId/previewId/token — so importContactsFile re-posts the file to commit.
// No writes; confirm via importContactsFile.
export async function previewContactsFile(form) {
  const headers = await authHeader();
  try {
    const res = await axios.post(`${T6_BASE}/api/gtm/contacts/preview`, form, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// POST /api/gtm/contacts/import — commit a previewed upload into the enrichment
// pipeline. /contacts/preview returns no server-side staging handle, so this
// re-posts the file itself (FormData) plus the list `label` — that re-post IS the
// contract. The lightweight { uploadId } branch is left DORMANT: it never fires
// today (preview returns no token) but stays so a future staging backend is a
// one-line rewire. Returns res.data { imported, queued, suppressedAtIntake }.
// The field name is `sourceLabel`. commitContactImport reads body.sourceLabel (falling
// back to body.source) and 400s source_label_required when it is blank. This used to
// append `label`, which the backend never reads, so every CSV import 400d. It is stored
// as the name of the list these contacts belong to, so it has to be a real name, not a
// filler value the client makes up.
export async function importContactsFile({ form, sourceLabel, uploadId } = {}) {
  const headers = await authHeader();
  try {
    // Dormant: no preview returns an uploadId today, so this branch never runs.
    if (uploadId) {
      const res = await axios.post(`${T6_BASE}/api/gtm/contacts/import`, { uploadId, sourceLabel }, { headers });
      return res.data;
    }
    if (form && typeof form.has === 'function' && !form.has('sourceLabel')) {
      form.append('sourceLabel', sourceLabel || '');
    }
    const res = await axios.post(`${T6_BASE}/api/gtm/contacts/import`, form, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// GET /api/gtm/apollo/lists — saved Apollo lists already in the paid account:
// [{ id, name, count }]. Tolerates a bare array.
export async function fetchApolloLists() {
  const headers = await authHeader();
  try {
    const res = await axios.get(`${T6_BASE}/api/gtm/apollo/lists`, { headers });
    return Array.isArray(res.data && res.data.lists) ? res.data.lists : (Array.isArray(res.data) ? res.data : []);
  } catch (err) {
    throw gtmActionError(err);
  }
}

// GET /api/gtm/apollo/lists/:id/contacts — a preview page of one saved list.
// Returns res.data { contacts, total, page }.
export async function previewApolloList(id, page = 1) {
  const headers = await authHeader();
  try {
    const res = await axios.get(`${T6_BASE}/api/gtm/apollo/lists/${id}/contacts`, { headers, params: { page } });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// POST /api/gtm/apollo/lists/:id/import — import a saved Apollo list into the
// pipeline. These contacts are already revealed and paid for (no extra charge).
// Returns res.data { imported, queued, suppressedAtIntake }.
// importApolloList posted an empty body, so the saved-list path was broken in exactly
// the same way as the CSV one: importApolloList requires sourceLabel and 400s
// source_label_required without it.
export async function importApolloList(id, sourceLabel) {
  const headers = await authHeader();
  try {
    const res = await axios.post(`${T6_BASE}/api/gtm/apollo/lists/${id}/import`, { sourceLabel: sourceLabel || '' }, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// ─── Test as a target: dry-run one prospect + send-to-my-inbox ───────────────
// Both endpoints ship in the T6 chunk running in parallel; until it deploys they
// 404. Callers treat a 404/501 as "not available yet" (fail soft inline) rather
// than a hard error, and never blank the page on it.

// POST /api/gtm/test-target — start ONE prospect through the full pipeline WITHOUT
// writing or sending: evidence, email discovery, suppression, fit check, routing,
// assembled email. Body { linkedinUrl, name?, company?, title?, sequenceKey? } (omit
// sequenceKey / send '' for Auto-route). The run is now ASYNC: this returns 202 with
// { runId }. Poll getTestTargetRun(runId) for progress and the final result. (An older
// backend may still return the finished result inline; callers handle both shapes.)
export async function testTarget(payload) {
  const headers = await authHeader();
  try {
    const res = await axios.post(`${T6_BASE}/api/gtm/test-target`, payload, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// GET /api/gtm/test-target/:runId — poll a running test. Resolves
// { status: 'running'|'done'|'failed', stage?, ... }. On 'done' the stage result
// fields (evidence, emailFound, email, suppression, fitVerdict, routedSequence,
// assembledEmail, ...) arrive either at the top level or under `result`; on 'failed'
// the reason arrives as `error`/`reason`. A 404/501 (endpoint not deployed) is treated
// by callers as "not available yet" (fail soft inline), not a hard error.
export async function getTestTargetRun(runId) {
  const headers = await authHeader();
  try {
    const res = await axios.get(`${T6_BASE}/api/gtm/test-target/${encodeURIComponent(runId)}`, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// POST /api/gtm/test-send — send ONE assembled test email to a chosen inbox (yours).
// Body { email, evidence?, sequenceKey?, step? } — the target-test passes the run's
// evidence; the sequence Preview passes its sampleContact with no evidence. Resolves
// res.data on send; a hold (e.g. link_asset_no_default) comes back coded — either as
// a thrown gtmActionError or an { ok:false, reason } body — so the caller renders it
// inline, not as a success toast.
export async function testSend(payload) {
  const headers = await authHeader();
  try {
    const res = await axios.post(`${T6_BASE}/api/gtm/test-send`, payload, { headers });
    return res.data;
  } catch (err) {
    throw gtmActionError(err);
  }
}

// useGtmContacts — the Contacts browse view: every imported contact with its pipeline
// state. GET /api/gtm/contacts?status=&page= -> { page, pageSize, total, totalPages,
// contacts }; tolerates a bare array.
//
// GTM-CONTACTS-PAGING-01: this used to send { limit, offset }. listImportedContacts
// reads NEITHER: it pages on `page` with a server-owned CONTACTS_PAGE_SIZE. So every
// request returned page 1 sorted createdAt desc, Previous and Next silently refetched
// that same page, and any contact past the first server page was unreachable. That is
// how held contacts "vanished": nothing filters them out, they just aged off page one
// as newer contacts arrived and there was no working way to page back to them.
//
// `status` is passed through so a held contact can be found directly rather than paged
// for. The server applies it; an empty status means no filter, exactly as before.
export function useGtmContacts() {
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(null);   // server-owned, reported back
  const [totalPages, setTotalPages] = useState(null);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(
    async (nextPage, statusFilter) => {
      setLoading(true);
      setError(null);
      try {
        const headers = await authHeader();
        const params = { page: Math.max(1, nextPage) };
        if (statusFilter) params.status = statusFilter;
        const res = await axios.get(`${T6_BASE}/api/gtm/contacts`, { headers, params });
        const d = res.data || {};
        const rows = Array.isArray(d.contacts) ? d.contacts : Array.isArray(d) ? d : [];
        setContacts(rows);
        setTotal(d.total != null ? d.total : null);
        setPageSize(d.pageSize != null ? d.pageSize : null);
        setTotalPages(d.totalPages != null ? d.totalPages : null);
        setPage(Math.max(1, nextPage));
      } catch (err) {
        setError(readError(err));
        setContacts([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    load(1, status);
  }, [load, status]);

  const reload = useCallback(() => load(page, status), [load, page, status]);
  const next = useCallback(() => load(page + 1, status), [load, page, status]);
  const prev = useCallback(() => load(Math.max(1, page - 1), status), [load, page, status]);
  const setStatusFilter = useCallback((s) => setStatus(s || ''), []);

  const hasNext = totalPages != null ? page < totalPages : contacts.length > 0;
  return {
    contacts, total, page, pageSize, totalPages, hasNext, status,
    loading, error, reload, next, prev, setStatusFilter,
  };
}

export default useGtmResource;
